import type { JourneyTestNotificationRequest } from 'omega-rtc/actions/journey-test-notification';
import { JOURNEY_TEST_NOTIFICATION_CSV_LIMITS } from 'omega-rtc/config';
import { findJourneyTemplates, isJourneyAttributesObject, JourneyGroupInput, JourneyTemplateMatch } from './journey-template-matcher';

export type JourneyCsvLimits = { maxBytes: number; maxRows: number; maxColumns: number };
export type JourneyCsvRecord = { row: number; attributes: Record<string, unknown>; groupID?: string; error?: string };
export type JourneyCsvResult = JourneyCsvRecord & { countryID?: number; candidates: Extract<JourneyTemplateMatch, { status: 'matched' }>[]; error?: string };

/** Comma-separated UTF-8 CSV: quoted commas/newlines and escaped double quotes. */
const parseCells = (input: string): string[][] => {
  const rows: string[][] = []; let row: string[] = []; let cell = '';
  let state: 'start' | 'plain' | 'quoted' | 'closed' = 'start'; let explicit = false;
  const finishCell = () => { row.push(cell); cell = ''; state = 'start'; };
  const finishRow = () => {
    finishCell();
    if (explicit || row.length > 1 || row[0].trim()) rows.push(row);
    row = []; explicit = false;
  };
  const text = input.replace(/^\uFEFF/, '');
  if (text.includes('\0')) throw new Error('CSV must not contain NUL bytes.');
  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    if (state === 'quoted') {
      if (char === '"') {
        if (text[i + 1] === '"') { cell += '"'; i += 1; } else state = 'closed';
      } else cell += char;
    } else if (char === ',') { finishCell(); explicit = true; } else if (char === '\r' || char === '\n') {
      finishRow(); if (char === '\r' && text[i + 1] === '\n') i += 1;
    } else if (state === 'closed') {
      if (char !== ' ' && char !== '\t') throw new Error('CSV contains invalid text after a quoted field.');
    } else if (char === '"') {
      if (state !== 'start') throw new Error('CSV contains an unexpected quote.');
      state = 'quoted'; explicit = true;
    } else { cell += char; state = 'plain'; }
  }
  if (state === 'quoted') throw new Error('CSV contains an unterminated quoted field.');
  if (cell || row.length || explicit || state === 'closed') finishRow();
  return rows;
};

const safeJsonValue = (value: unknown, depth = 0): boolean => {
  if (depth > 20) return false;
  if (value === null || typeof value === 'string' || typeof value === 'boolean') return true;
  if (typeof value === 'number') return Number.isFinite(value);
  if (Array.isArray(value)) return value.every(item => safeJsonValue(item, depth + 1));
  return isJourneyAttributesObject(value) && Object.values(value).every(item => safeJsonValue(item, depth + 1));
};

export const parseJourneyNotificationCsv = (
  text: string,
  limits: JourneyCsvLimits = JOURNEY_TEST_NOTIFICATION_CSV_LIMITS,
): JourneyCsvRecord[] => {
  const matrix = parseCells(text);
  if (matrix.length < 2) throw new Error('CSV must contain a header and at least one data record.');
  const headers = matrix[0].map(header => header.trim());
  if (headers.length > limits.maxColumns) throw new Error(`CSV supports at most ${limits.maxColumns} columns.`);
  if (headers.some(header => !header || header.length > 256
    || Array.from(header).some(char => char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127))) {
    throw new Error('CSV requires non-empty attribute headers without control characters.');
  }
  if (new Set(headers).size !== headers.length) throw new Error('CSV headers must be unique.');
  const attributeHeaders = headers.filter(header => header !== 'groupID');
  if (!attributeHeaders.length) throw new Error('CSV requires attribute columns or a userAttributes JSON column.');
  const jsonColumn = headers.indexOf('userAttributes');
  if (jsonColumn >= 0 && attributeHeaders.length !== 1) {
    throw new Error('Use either attribute columns or a userAttributes JSON column, plus optional groupID.');
  }
  if (matrix.length - 1 > limits.maxRows) throw new Error(`CSV supports at most ${limits.maxRows} data records.`);
  const groupColumn = headers.indexOf('groupID');
  return matrix.slice(1).map((cells, index) => {
    const row = index + 1;
    const groupID = groupColumn >= 0 ? (cells[groupColumn] || '').trim() || undefined : undefined;
    const invalid = (error: string): JourneyCsvRecord => ({ row, attributes: {}, groupID, error });
    if (cells.length !== headers.length) return invalid(`CSV row ${row} has ${cells.length} fields; expected ${headers.length}.`);
    if (jsonColumn >= 0) {
      try {
        const attributes = JSON.parse(cells[jsonColumn]);
        if (!isJourneyAttributesObject(attributes) || !safeJsonValue(attributes)) {
          return invalid(`CSV row ${row} userAttributes must be a finite JSON object with depth at most 20.`);
        }
        if (Object.keys(attributes).length > limits.maxColumns) return invalid(`CSV row ${row} has too many attributes.`);
        return { row, attributes, groupID };
      } catch (_) { return invalid(`CSV row ${row} userAttributes must contain valid JSON.`); }
    }
    // CSV has no native types. Preserve text/IDs; blank cells represent absent attributes.
    const entries = headers.map((header, i) => [header, cells[i]] as const)
      .filter(([header, value]) => header !== 'groupID' && value !== '');
    if (!entries.length) return invalid(`CSV row ${row} contains no user attributes.`);
    return { row, attributes: Object.fromEntries(entries), groupID };
  });
};

export const validateJourneyCsvFile = (file: Pick<File, 'name' | 'type' | 'size'>, limits: JourneyCsvLimits) => {
  const mime = file.type.toLowerCase().split(';')[0];
  if (!/\.csv$/i.test(file.name) || !['', 'text/csv', 'text/plain', 'application/csv', 'application/vnd.ms-excel'].includes(mime)) {
    throw new Error('Select one CSV file (.csv).');
  }
  if (!file.size) throw new Error('CSV file must not be empty.');
  if (file.size > limits.maxBytes) throw new Error(`CSV file exceeds the ${Math.floor(limits.maxBytes / 1024)} KiB limit.`);
};

export const decodeJourneyCsv = (buffer: ArrayBuffer): string => {
  try { return new TextDecoder('utf-8', { fatal: true }).decode(buffer); } catch (_) { throw new Error('CSV must be UTF-8 encoded.'); }
};

export const resolveJourneyCsvRecords = (records: JourneyCsvRecord[], groups: JourneyGroupInput[], countryID?: number): JourneyCsvResult[] => (
  records.map(record => {
    const base = { ...record, countryID, candidates: [] };
    if (record.error) return base;
    const scopedGroups = record.groupID ? groups.filter(group => group && group.groupID === record.groupID) : groups;
    if (record.groupID && !scopedGroups.length) return { ...base, error: 'The CSV Group ID is not part of this campaign.' };
    const results = findJourneyTemplates(scopedGroups, record.attributes);
    const candidates = results.filter((result): result is Extract<JourneyTemplateMatch, { status: 'matched' }> => result.status === 'matched');
    if (candidates.length === 1) return { ...base, candidates };
    if (candidates.length > 1) return { ...base, candidates, error: 'Multiple matching variants found. Select one template for this record.' };
    const invalid = results.find(result => result.status === 'invalid');
    if (invalid && invalid.status !== 'matched') return { ...base, error: invalid.message };
    const completeVariant = scopedGroups.some(group => (group.variants || []).some(variant => (
      variant.criteria && Object.keys(variant.criteria).every(key => Object.prototype.hasOwnProperty.call(record.attributes, key))
    )));
    return { ...base, error: completeVariant ? 'No matching template criteria found.' : 'Missing required attributes for available template variants.' };
  })
);

// Canonicalize JSON object order, preserving scalar types and array order.
const stableJson = (value: unknown): string => {
  if (Array.isArray(value)) return `[${value.map(stableJson).join(',')}]`;
  if (isJourneyAttributesObject(value)) return `{${Object.keys(value).sort().map(key => `${JSON.stringify(key)}:${stableJson(value[key])}`).join(',')}}`;
  return JSON.stringify(value);
};
export const journeyCsvRequestKey = (request: JourneyTestNotificationRequest) => stableJson(request);

export const JOURNEY_NOTIFICATION_SAMPLE_CSV = `userAttributes\r\n${
  ['1', '2', '3'].map(week => `"${JSON.stringify({ pax_country_code: 'my', week_count: week }).replace(/"/g, '""')}"`).join('\r\n')}\r\n`;
